import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { EASE, Reveal, SectionHead } from "@/components/site/motion";

const CAPABILITIES = [
  {
    title: "Strategy & Opportunity",
    desc: "Define where partnerships can create measurable business growth.",
    items: [
      "Partnership strategy aligned to business and revenue priorities",
      "Market, ecosystem, and whitespace analysis",
      "Partner segmentation and prioritization",
      "Build, buy, or partner assessments",
      "Partner program audits and turnaround strategies",
    ],
  },
  {
    title: "Program Design & Economics",
    desc: "Build a partnership model that works for both sides and makes economic sense.",
    items: [
      "Partner models, tiers, and program structure",
      "Partner value proposition and benefits",
      "Incentive, revenue-share, and commercial models",
      "Partnership business cases and ROI",
      "Partner profitability and investment strategy",
    ],
  },
  {
    title: "Operating Model",
    desc: "Put the structure, processes, and accountability in place to make partnerships scalable.",
    items: [
      "Partner lifecycle and engagement model",
      "Roles, ownership, and cross-functional responsibilities",
      "Partner vetting, contracting, and onboarding",
      "CRM/PRM workflows, reporting, and attribution",
      "Governance, QBRs, scorecards, and executive dashboards",
    ],
  },
  {
    title: "GTM & Activation",
    desc: "Turn signed partnerships into pipeline, revenue, and market expansion.",
    items: [
      "Partner recruitment and activation",
      "Joint business planning and co-sell strategy",
      "Partner enablement and sales plays",
      "Partner-sourced pipeline development",
      "Joint campaigns and market-entry programs",
      "Cross-sell and upsell strategies",
    ],
  },
  {
    title: "Performance & Scale",
    desc: "Determine what is working, where to invest, and how to scale it.",
    items: [
      "Partner performance and portfolio management",
      "Pipeline, revenue, conversion, and ROI measurement",
      "Partner-sourced and partner-influenced attribution",
      "Underperforming partner remediation",
      "Expansion into new partners, markets, and use cases",
      "Repeatable playbooks for scalable growth",
    ],
  },
];

const LEADERSHIP = [
  "Hands-on program ownership and execution",
  "Leadership and coaching of partnership teams",
  "Cross-functional alignment across Sales, Marketing, Product, Finance, and Operations",
  "Executive and Board-level communication",
  "Strategic planning through day-to-day execution",
];

const WHEN_FRACTIONAL = [
  "Partnerships need strategy and senior ownership before the permanent org structure is defined",
  "The company is between partnership leaders and needs continuity during a search",
  "Leadership needs an honest, outside assessment before committing to a hire",
  "A specific initiative (market entry, a major integration, a channel launch) needs senior ownership on a defined timeline",
  "An existing function needs to be rebuilt or restructured before it’s ready for permanent leadership",
];

const OFFERS = [
  {
    title: "Fractional Partnership Leadership",
    text: "Ongoing, part-time ownership when the business needs senior partnership leadership and execution, but the scope does not yet require a permanent executive.",
  },
  {
    title: "Interim Partnership Leadership",
    text: "Full leadership coverage during a search or transition, so the program keeps moving while the company hires for the permanent role.",
  },
  {
    title: "Partner Program Build & Transformation",
    text: "A defined engagement to launch, rebuild, or monetize a partnership function, whether the need is a new channel, a market entry, a major integration, or a partner-program turnaround.",
  },
];

const PHASES = [
  {
    when: "Days 1–30",
    title: "Diagnose & Prioritize",
    items: [
      "Assess the current state: existing program, partner activity, or starting point",
      "Interview key stakeholders across Sales, Marketing, Product, and leadership",
      "Identify gaps, market opportunities, quick wins, underperforming relationships, and untapped potential",
      "Prioritize where partnerships can create the greatest business impact",
    ],
  },
  {
    when: "Days 31–60",
    title: "Design & Build",
    items: [
      "Build Partner Playbook V1, including segmentation, target partner profile, and lifecycle",
      "Define KPIs, scorecards, attribution, and executive reporting",
      "Develop incentive, commercial, and engagement models",
      "Define organizational ownership, cross-functional roles, decision rights, and governance",
      "Create a prioritized partner roadmap based on strategic fit and revenue potential",
    ],
  },
  {
    when: "Days 61–90",
    title: "Activate & Advance",
    items: [
      "Begin outreach, activation, and negotiation with priority partners",
      "Move highest-value opportunities into active partnership development",
      "Launch reporting, governance, and executive visibility",
      "Establish the operating cadence for pipeline reviews, partner performance, and QBRs",
    ],
  },
  {
    when: "Day 90 and beyond",
    title: "Scale & Decide",
    items: [
      "Continue fractional leadership as the program gains traction",
      "Expand into new partners, markets, channels, or use cases",
      "Optimize performance based on early data and partner response",
      "Use early results, pipeline, operating maturity, and organizational readiness to determine the next phase of investment and leadership",
    ],
  },
];

export default function Services() {
  return (
    <>
      <section className="page-hero" data-testid="services-hero">
        <div className="wrap">
          <motion.span
            className="kicker"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            The approach
          </motion.span>
          <h1 style={{ fontSize: "clamp(2rem, 3.6vw, 2.9rem)" }} data-testid="services-heading">
            <span className="mask-line">
              <motion.span
                className="mask-inner"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              >
                Partnership leadership from strategy to scale.
              </motion.span>
            </span>
          </h1>
          <motion.p
            className="lede"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
          >
            The five capability areas this work draws on, the case for fractional leadership, the ways to bring it in,
            and a rough shape of the first 90 days.
          </motion.p>
        </div>
      </section>

      <section className="section-border-top" data-testid="services-capabilities">
        <div className="wrap">
          <SectionHead
            kicker="Capabilities"
            title="From strategy to scale, I build, fix, and monetize partnership programs."
            testid="services-capabilities-head"
          />
          <div className="cap-list">
            {CAPABILITIES.map((cap, i) => (
              <Reveal key={cap.title} y={22} className="cap-row" data-testid={`services-cap-row-${i}`}>
                <div className="cap-row-head">
                  <span className="cap-num">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3>{cap.title}</h3>
                    <p className="cap-desc">{cap.desc}</p>
                  </div>
                </div>
                <ul>
                  {cap.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal y={24} className="leadership-panel" data-testid="leadership-panel">
            <div className="leadership-panel-head">
              <span className="kicker">Leadership &amp; execution</span>
              <h3>Senior partnership leadership from strategy through execution.</h3>
              <p>
                I can operate as a Fractional Head of Partnerships, Interim Director or VP, or embedded partnership
                leader, providing:
              </p>
            </div>
            <ul className="leadership-list">
              {LEADERSHIP.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="section-border-top" data-testid="services-why-fractional">
        <div className="wrap">
          <SectionHead
            kicker="Why fractional"
            title="The right leadership model for where the business is today."
            testid="services-fractional-head"
          />
          <div className="fractional-layout">
            <Reveal y={22}>
              <p>
                Fractional leadership gets senior partnership expertise in place quickly, focused on a specific
                commercial objective, without the ramp time or commitment of a full-time search. It also brings an
                outside perspective: someone who has built this function before and can assess, prioritize, and start
                executing in weeks rather than a hiring quarter.
              </p>
              <p>
                This works particularly well when the business needs senior partnership ownership now, but the
                function, strategy, or operating model is still taking shape. As the program matures, the role can
                continue fractionally or transition into permanent leadership.
              </p>
              <p>
                This is hands-on execution, not advisory-only consulting. The work is building the strategy, then
                running it: recruiting partners, setting up the scorecards, and sitting in the QBRs.
              </p>
            </Reveal>
            <Reveal y={22} delay={0.12} className="callout" data-testid="when-fractional-callout">
              <h4>When fractional leadership makes sense</h4>
              <ul>
                {WHEN_FRACTIONAL.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-border-top" data-testid="services-engagement">
        <div className="wrap">
          <SectionHead
            kicker="Engagement"
            title="Three ways to bring this in."
            lede="The right structure depends on the company’s stage and the commercial problem at hand."
            testid="services-engagement-head"
          />
          <div className="offer-grid">
            {OFFERS.map((o, i) => (
              <Reveal key={o.title} y={20} delay={i * 0.08} className="offer-item" data-testid={`offer-item-${i}`}>
                <h3>{o.title}</h3>
                <p>{o.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-border-top" data-testid="services-timeline">
        <div className="wrap">
          <SectionHead
            kicker="What to expect"
            title="The first 90 days."
            lede="The first 90 days are about creating clarity, building the operating foundation, and putting the highest-value opportunities into motion. By Day 90, the program should have clear priorities, a working playbook, defined metrics, and active partner conversations underway."
            testid="services-timeline-head"
          />
          <div className="timeline">
            {PHASES.map((ph, i) => (
              <Reveal key={ph.when} y={24} delay={i * 0.05} className="timeline-phase" data-testid={`timeline-phase-${i}`}>
                <div>
                  <span className="timeline-when">{ph.when}</span>
                  <h3>{ph.title}</h3>
                </div>
                <ul>
                  {ph.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
          <Reveal y={18}>
            <p className="muted" data-testid="timeline-closing" style={{ marginTop: "2rem", maxWidth: "64ch" }}>
              By Day 90 the strategy, operating foundation, organizational ownership, priorities, and highest-value
              opportunities are in motion, giving leadership enough visibility to make an informed go-forward decision.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="cta-band" data-testid="services-cta-section">
        <div className="wrap">
          <Reveal y={26}>
            <span className="kicker" style={{ color: "var(--brass-bright)" }}>
              <span className="chapter" style={{ color: "var(--brass)" }}>Next step</span>
              Start the conversation
            </span>
            <h2>Let’s talk about what your partnerships could be producing.</h2>
            <p>
              Bring the problem you’re facing, whatever stage it’s at, and we’ll work out whether fractional leadership
              is the right next step.
            </p>
            <div className="btn-row">
              <Link to="/contact" className="btn btn-on-ink" data-testid="services-cta-primary">
                Discuss a Partnership Challenge <span className="btn-arrow">→</span>
              </Link>
            </div>
            <p className="cta-note" data-testid="services-cta-note">
              Available for fractional, interim, and defined partner-transformation engagements.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
